import React, { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, Text, Legend } from "recharts";
import styles from "./index.module.css";
import { useNavigate } from "react-router";
import PuffLoader from "react-spinners/PuffLoader";

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
  name,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <Text
      x={x}
      y={y}
      fill="black"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={10}
      fontWeight="bold"
      style={{ pointerEvents: "none" }}
    >
      {value}
    </Text>
  );
};

const PieChartComponent = (props) => {
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const navigate = useNavigate();
  const showLabels = props.showLabels !== undefined ? props.showLabels : true;
  const showLegend = props.showLegend !== undefined ? props.showLegend : true;

  useEffect(() => {
    const updateChartWidth = () => {
      if (chartContainerRef.current) {
        setChartWidth(chartContainerRef.current.offsetHeight);
      }
    };

    updateChartWidth();

    window.addEventListener("resize", updateChartWidth);
    return () => window.removeEventListener("resize", updateChartWidth);
  }, [props]);

  // Handler function for pie slice clicks
  const handlePieClick = (data, index) => {
    const baseUrl = `${props?.url}?${
      props?.customParam?.toLowerCase()
        ? props?.customParam?.toLowerCase()
        : "name"
    }=${data?.name?.toLowerCase()}`;
    
    // Add locations parameter if it exists
    const finalUrl = props.location && props.location.length > 0
      ? `${baseUrl}&location=${props.location.join(',')}`
      : baseUrl;
    
    navigate(finalUrl);
  };

  return props.data.length > 0 ? (
    <div
      className={styles["chart-container"]}
      ref={chartContainerRef}
      style={{ outline: "none" }}
    >
      <PieChart
        width={chartWidth}
        height={chartWidth}
        style={{ outline: "none" }}
      >
        <Pie
          data={props?.data}
          cx="50%"
          cy="50%"
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
          label={showLabels ? renderCustomLabel : undefined}
          labelLine={false}
          onClick={(data, index) => handlePieClick(data, index)}
          style={{ outline: "none" }}
        >
          {props?.data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color}
            />
          ))}
        </Pie>
      </PieChart>
      {/* Custom legend below the chart */}
      {showLegend && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 10,
          marginTop: 8,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          maxWidth: 420,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 10, height: 10, background: '#FF7F50', borderRadius: 3, display: 'inline-block' }}></span>
            <span style={{ fontSize: 10 }}>Intl Airside</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 10, height: 10, background: '#36A2EB', borderRadius: 3, display: 'inline-block' }}></span>
            <span style={{ fontSize: 10 }}>Intl Landside</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 10, height: 10, background: '#4BCF8F', borderRadius: 3, display: 'inline-block' }}></span>
            <span style={{ fontSize: 10 }}>Domestic Landside</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 10, height: 10, background: '#FFD166', borderRadius: 3, display: 'inline-block' }}></span>
            <span style={{ fontSize: 10 }}>Domestic Airside</span>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div className="loader-container">
      <PuffLoader />
    </div>
  );
};

export default PieChartComponent;
